import { useEffect, useState } from "react";
import {
  DayBookReportData,
  reportsService,
} from "../../services/reports.service";
import { storeService } from "../../services/store.service";
import { useAuthStore } from "../../stores/authStore";
import { formatBusinessDate } from "../../lib/utils";

export default function DayBookReportPage() {
  const { selectedStoreId } = useAuthStore();
  const [stores, setStores] = useState<Array<{ id: string; name: string }>>([]);
  const [filters, setFilters] = useState({
    startDate: formatBusinessDate(new Date()),
    endDate: formatBusinessDate(new Date()),
    storeId: selectedStoreId || "",
  });
  const [reportData, setReportData] = useState<DayBookReportData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    storeService
      .getAll()
      .then(setStores)
      .catch(() => {});
  }, []);

  const formatCurrency = (amount: number) => `₹${amount.toFixed(2)}`;

  const loadReport = async () => {
    setLoading(true);
    try {
      const data = await reportsService.getDayBookReport({
        startDate: filters.startDate,
        endDate: filters.endDate,
        storeId: filters.storeId || undefined,
      });
      setReportData(data);
    } catch (error) {
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Day Book</h1>
        <p className="text-gray-600">Daily inflow and outflow summary</p>
      </div>

      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <h2 className="text-lg font-semibold mb-4">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) =>
                setFilters({ ...filters, startDate: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) =>
                setFilters({ ...filters, endDate: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Store
            </label>
            <select
              value={filters.storeId}
              onChange={(e) =>
                setFilters({ ...filters, storeId: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Stores</option>
              {stores.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-4">
          <button
            onClick={loadReport}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            {loading ? "Loading..." : "Generate Report"}
          </button>
        </div>
      </div>

      {reportData && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white p-4 rounded-lg shadow">
              <p className="text-sm text-gray-500">Total Sales</p>
              <p className="text-xl font-semibold text-gray-900">
                {formatCurrency(reportData.summary.totalSales)}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <p className="text-sm text-gray-500">Total Returns</p>
              <p className="text-xl font-semibold text-red-600">
                {formatCurrency(reportData.summary.totalReturns)}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <p className="text-sm text-gray-500">Net Inflow</p>
              <p className="text-xl font-semibold text-emerald-700">
                {formatCurrency(reportData.summary.netInflow)}
              </p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-lg font-semibold mb-4">Entries</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-gray-500">
                    <th className="text-left py-2">Date</th>
                    <th className="text-left py-2">Type</th>
                    <th className="text-left py-2">Reference</th>
                    <th className="text-left py-2">Party</th>
                    <th className="text-left py-2">Method</th>
                    <th className="text-right py-2">Direction</th>
                    <th className="text-right py-2">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.entries.map((entry) => (
                    <tr key={entry.id} className="border-b">
                      <td className="py-2">
                        {new Date(entry.date).toLocaleString("en-IN", {
                          timeZone: "Asia/Kolkata",
                        })}
                      </td>
                      <td className="py-2">{entry.type}</td>
                      <td className="py-2">{entry.reference}</td>
                      <td className="py-2">{entry.party}</td>
                      <td className="py-2">{entry.paymentMethod}</td>
                      <td className="py-2 text-right">
                        <span
                          className={
                            entry.direction === "IN"
                              ? "text-emerald-600"
                              : "text-red-600"
                          }
                        >
                          {entry.direction}
                        </span>
                      </td>
                      <td className="py-2 text-right font-medium">
                        {formatCurrency(entry.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
